import styled from '@emotion/styled';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ButtonSpinner from '@/components/common/elements/ButtonSpinner';
import { FORM_COLOR as C } from '@/pages/Seminar/components/form/formStyles';

/**
 * 홈페이지 대표 사진 후보 고르기 — 가입·치과 정보 수정·AI 대화 카드가 같이 쓴다.
 * 사진은 서버가 만든 작은 미리보기(data URI)를 먼저 쓰고, 없으면 원래 주소로 보여 준다. 고르면 [onPick] — 저장은 부르는 쪽(서버가 내려받아 저장).
 */
interface HomepagePhotoPickerProps {
    candidates: string[];
    previews?: (string | null)[];
    /** 지금 저장 중인 사진 */
    pickingUrl?: string | null;
    /** 이미 고른 사진(원래 주소) */
    pickedUrl?: string | null;
    disabled?: boolean;
    onPick: (url: string) => void;
}

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const Tile = styled.button<{ selected: boolean }>`
    position: relative;
    display: block;
    width: 100%;
    aspect-ratio: 16 / 9;
    padding: 0;
    border: ${(p) => (p.selected ? `2px solid ${C.primary}` : `1px solid ${C.line}`)};
    border-radius: 12px;
    overflow: hidden;
    background: ${C.lineSoft};
    cursor: pointer;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }

    img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

/** 고른 표시·저장 중 표시 — 28px 원 + 18px 아이콘(UI 가이드 4.1) */
const Badge = styled.span`
    position: absolute;
    top: 6px;
    right: 6px;
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: ${C.primary};
    color: #fff;

    svg {
        display: block;
        width: 18px;
        height: 18px;
    }
`;

const HomepagePhotoPicker = ({ candidates, previews = [], pickingUrl = null, pickedUrl = null, disabled = false, onPick }: HomepagePhotoPickerProps) => (
    <Grid role="list" aria-label="홈페이지 사진 후보">
        {candidates.map((url, index) => {
            const selected: boolean = url === pickedUrl;
            const busy: boolean = url === pickingUrl;
            return (
                <Tile
                    key={url}
                    type="button"
                    role="listitem"
                    selected={selected}
                    disabled={disabled || !!pickingUrl}
                    aria-pressed={selected}
                    aria-label={`홈페이지 사진 ${index + 1}${selected ? ' (고름)' : ''}`}
                    onClick={() => onPick(url)}
                >
                    <img src={previews[index] ?? url} alt="" loading="lazy" referrerPolicy="no-referrer" />
                    {busy && <Badge><ButtonSpinner /></Badge>}
                    {!busy && selected && <Badge><CheckRoundedIcon aria-hidden /></Badge>}
                </Tile>
            );
        })}
    </Grid>
);

export default HomepagePhotoPicker;
